/* Copyright 2025 Supovia LLC */
import getApiUrl from '@supovia/shared/getApiUrl.js'
import {
  getAccessToken,
  isAccessTokenExpired,
  setAccessToken,
} from './accessToken.js'
import {
  getRefreshToken,
  isRefreshTokenExpired,
  setRefreshToken,
} from './refreshToken.js'

// The Supovia widget runs EMBEDDED in other products' pages: on refresh
// failure the HOST app re-mints tokens via this callback instead of us
// navigating the host page away.
let onRefreshFailed = null

export function setRefreshFailedCallback(callback) {
  onRefreshFailed = callback
}

export const setRefreshFailedCallbackForSupoviaClient =
  setRefreshFailedCallback

// A dead refresh token is terminal: no retry can mint tokens again, the only
// way forward is a clean re-auth. When no host callback is registered,
// navigate the CURRENT tab to /logout — window.open here would be
// popup-blocked in async fetch continuations (no user activation) and, in the
// WKWebView shells, bounced to an external browser where the logout can't
// converge back into the app. Guarded to once per page lifetime: every
// in-flight request hits this simultaneously.
let routedToLogout = false

function routeToLogout() {
  if (typeof window !== 'undefined' && !routedToLogout) {
    routedToLogout = true
    window.location.href = '/logout'
  }
}

export default async function refreshAccessToken() {
  let accessToken = getAccessToken()

  if (accessToken && isAccessTokenExpired()) {
    if (isRefreshTokenExpired()) {
      if (onRefreshFailed) {
        await onRefreshFailed()
        return getAccessToken()
      }

      routeToLogout()

      throw new Error('refresh token has expired')
    } else {
      let refreshToken = getRefreshToken()

      const url = `${getApiUrl()}/api/refresh-access-token`

      let result

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Token ${btoa(refreshToken)}`,
          },
        })

        if (!response.ok) {
          // Never rethrow the parsed body itself: a plain object interpolates
          // as "[object Object]" wherever it bubbles and is unreadable in
          // Error Tracking. Wrap it in a real Error instead.
          const body = await response.json().catch(() => ({}))

          // The server rejected the refresh token outright: a 401 (user
          // deleted, token revoked) or a `jwt expired` body (the token lapsed
          // server-side — the API maps that to a 500, but at THIS endpoint it
          // can only mean the refresh token itself is dead). Either way no
          // retry can recover — the catch below routes re-auth through the
          // host callback when registered, else /logout.
          if (
            response.status === 401 ||
            /jwt expired/i.test(body.message || '')
          ) {
            const error = new Error(
              `refresh token rejected: ${
                body.message || response.statusText || response.status
              }`,
            )
            error.status = response.status
            error.cause = body
            error.terminal = true
            throw error
          }

          const error = new Error(
            body.message || response.statusText || `${response.status}`,
          )
          error.status = body.status || response.status
          error.cause = body
          throw error
        }

        result = await response.json()
      } catch (error) {
        if (onRefreshFailed) {
          await onRefreshFailed()
          return getAccessToken()
        }

        if (error.terminal) {
          routeToLogout()
        }

        console.log(error)
        throw error
      }

      accessToken = result.accessToken
      refreshToken = result.refreshToken

      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
    }
  }

  return accessToken
}
